import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Alert,
  Fade,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import QuizIcon from "@mui/icons-material/Quiz";
import AddIcon from "@mui/icons-material/Add";
import BarChartIcon from "@mui/icons-material/BarChart";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import {
  getAllQuestions,
  toggleQuestionStatus,
  deleteQuestion,
  updateQuestion,
  createQuestion,
  reorderQuestions,
} from "../../api/questions-api";
import Modal from "../../components/pop-ups";
import { useToast } from "../../components/toaster";
// import { useCallback } from 'react';

const AdminQuestionsManagement = () => {
  const [questions, setQuestions] = useState([]);
  const [originalQuestions, setOriginalQuestions] = useState([]); // For reset functionality
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [listLoader, setListLoader] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [deleteQuestionId, setDeleteQuestionId] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({
    clue: '',
    hint: '',
    trivia: '',
    answer: '',
    sequence: 7,
    isActive: true
  });
  const [createFormData, setCreateFormData] = useState({
    clue: '',
    hint: '',
    trivia: '',
    answer: '',
    sequence: 7,
    isActive: true
  });
  const [filters, setFilters] = useState({
    isActive: '',
  });
  const { showToast } = useToast();

  const fetchQuestions = async () => {
    setListLoader(true);
    try {
      const filterParams = {};
      if (filters.isActive !== '') filterParams.isActive = filters.isActive;

      const response = await getAllQuestions(filterParams);
      const fetchedQuestions = response?.data?.data || [];
      setQuestions(fetchedQuestions);
      setOriginalQuestions([...fetchedQuestions]); // Store original order
      setHasUnsavedChanges(false);
    } catch (err) {
      showToast('Error fetching questions', 'error');
    } finally {
      setListLoader(false);
    }
  };

//   const fetchQuestions = useCallback(async () => {
//     setListLoader(true);
//     try {
//         const filterParams = {};
//         if (filters.isActive !== '') filterParams.isActive = filters.isActive;
//         const response = await getAllQuestions(filterParams);
//         const fetchedQuestions = response?.data?.data || [];
//         setQuestions(fetchedQuestions);
//         setOriginalQuestions([...fetchedQuestions]); // Store original order
//         setHasUnsavedChanges(false);
//     } catch (err) {
//         showToast('Error fetching questions', 'error');
//     } finally {
//         setListLoader(false);
//     }
// }, [filters,showToast]); // Add dependencies

// Now you only need ONE useEffect
    // useEffect(() => {
    // fetchQuestions();
    // }, [fetchQuestions]);

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [filters]);

  // Check if any filters are active
  const hasActiveFilters = filters.isActive !== '';
  
  // Drag and Drop Functions
  const handleDragStart = (e, index) => {
    if (hasActiveFilters) {
      e.preventDefault();
      return;
    }
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    if (hasActiveFilters) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    if (hasActiveFilters) return;
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    if (hasActiveFilters) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    
    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      setDragOverIndex(null);
      return;
    }

    const newQuestions = [...questions];
    const draggedQuestion = newQuestions[draggedItem];
    
    // Remove dragged item
    newQuestions.splice(draggedItem, 1);
    
    // Insert at new position
    newQuestions.splice(dropIndex, 0, draggedQuestion);
    
    setQuestions(newQuestions);
    setHasUnsavedChanges(true);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleSaveOrder = async () => {
    setIsSaving(true);
    try {
      // Extract question IDs in current order
      const questionIds = questions.map(q => q._id);
      
      await reorderQuestions(questionIds);
      
      // Update original questions to match new order
      setOriginalQuestions([...questions]);
      setHasUnsavedChanges(false);
      showToast('Question order saved successfully');
    } catch (err) {
      console.error("Failed to save order", err);
      showToast('Failed to save question order', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetOrder = () => {
    setQuestions([...originalQuestions]);
    setHasUnsavedChanges(false);
    showToast('Order reset to last saved state');
  };

  // Existing functions (keep all your existing handlers)
  const handleToggleStatus = async (questionId) => {
    try {
      await toggleQuestionStatus(questionId);
      showToast('Question status updated successfully');
      fetchQuestions();
    } catch (err) {
      console.error("Failed to toggle status", err);
      showToast('Failed to update question status', 'error');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      await deleteQuestion(questionId);
      showToast('Question deleted successfully');
      fetchQuestions();
    } catch (err) {
      console.error("Failed to delete question", err);
      showToast('Failed to delete question', 'error');
    }
    setOpenModal(false);
    setDeleteQuestionId(null);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setEditFormData({
      clue: question.clue || '',
      hint: question.hint || '',
      trivia: question.trivia || '',
      answer: Array.isArray(question.answer) ? question.answer.join(', ') : question.answer || '',
      sequence: question.sequence || 7,
      isActive: question.isActive || false
    });
    setOpenEditDialog(true);
  };

  const handleCreateQuestion = () => {
    const maxSequence = questions.length > 0 ? Math.max(...questions.map(q => q.sequence)) : 0;
    setCreateFormData({
      clue: '',
      hint: '',
      trivia: '',
      answer: '',
      sequence: maxSequence + 1,
      isActive: true
    });
    setOpenCreateDialog(true);
  };

  const handleUpdateQuestion = async () => {
    try {
      const updateData = { ...editFormData };
      updateData.type = 'text';
      
      if (updateData.answer.includes(',')) {
        updateData.answer = updateData.answer.split(',').map(item => item.trim());
      }

      if (!updateData.hint) delete updateData.hint;

      await updateQuestion(editingQuestion._id, updateData);
      showToast('Question updated successfully');
      fetchQuestions();
      setOpenEditDialog(false);
      setEditingQuestion(null);
    } catch (err) {
      showToast(`${err.response?.data?.message || "Failed to update Clue"}`, "error");
    }
  };

  const handleSaveNewQuestion = async () => {
    try {
      const createData = { ...createFormData };
      createData.type = 'text';
      
      if (createData.answer.includes(',')) {
        createData.answer = createData.answer.split(',').map(item => item.trim());
      }

      if (!createData.hint) delete createData.hint;

      await createQuestion(createData);
      showToast('Question created successfully');
      fetchQuestions();
      setOpenCreateDialog(false);
      setCreateFormData({
        clue: '',
        hint: '',
        trivia: '',
        answer: '',
        sequence: 1,
        isActive: true
      });
    } catch (err) {
      showToast(`${err.response?.data?.message || "Failed to create question"}`, "error");
    }
  };

  const handleFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateFormChange = (field, value) => {
    setCreateFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({ isActive: '' });
    // Reset any unsaved changes when clearing filters to avoid conflicts
    if (hasUnsavedChanges) {
      setQuestions([...originalQuestions]);
      setHasUnsavedChanges(false);
      showToast('Filters cleared and unsaved changes reset');
    }
  };

  return (
    <Box sx={{ padding: 4 }}>
      {/* Header */}
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: 600,
        }}
      >
        <QuizIcon /> Questions Management
      </Typography>

      {/* Unsaved Changes Alert */}
      <Fade in={hasUnsavedChanges}>
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                onClick={handleResetOrder}
                startIcon={<UndoIcon />}
                disabled={isSaving}
              >
                Reset
              </Button>
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={handleSaveOrder}
                startIcon={<SaveIcon />}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Order'}
              </Button>
            </Box>
          }
        >
          You have unsaved changes to the question order. Don't forget to save!
        </Alert>
      </Fade>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.isActive}
                label="Status"
                onChange={(e) => setFilters(prev => ({ ...prev, isActive: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={clearFilters}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateQuestion}
              fullWidth
            >
              Add Question
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Questions Table */}
      <Typography
        variant="subtitle1"
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: 600,
          fontSize: "1.2rem",
        }}
      >
        <BarChartIcon /> Questions List
        <IconButton size="small" onClick={fetchQuestions}>
          <RefreshIcon />
        </IconButton>
        {hasUnsavedChanges && !hasActiveFilters && (
          <Chip 
            label="Drag to reorder" 
            size="small" 
            color="info" 
            sx={{ ml: 2 }}
          />
        )}
        {hasActiveFilters && (
          <Chip 
            label="Clear filters to enable reordering" 
            size="small" 
            color="warning" 
            sx={{ ml: 2 }}
          />
        )}
      </Typography>

      {listLoader ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={1} sx={{ borderRadius: '5px' }}>
            <Table>
              <TableHead sx={{ backgroundColor: "#ccc" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center", borderRight: "1px solid #f0f0f0", width: 50 }}>
                    <DragIndicatorIcon />
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
                    #
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
                    SEQUENCE
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", borderRight: "1px solid #f0f0f0" }}>
                    CLUE
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
                    STATUS
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
                    ANSWER
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
                    ACTIONS
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {questions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No questions available
                    </TableCell>
                  </TableRow>
                ) : (
                  questions.map((question, index) => (
                    <TableRow 
                      key={question._id} 
                      hover
                      draggable={!hasActiveFilters}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      sx={{
                        cursor: hasActiveFilters ? 'default' : 'move',
                        backgroundColor: dragOverIndex === index && !hasActiveFilters ? '#f0f0f0' : 'inherit',
                        opacity: draggedItem === index && !hasActiveFilters ? 0.5 : 1,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <TableCell sx={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
                        <DragIndicatorIcon 
                          sx={{ 
                            color: hasActiveFilters ? '#ddd' : '#999', 
                            cursor: hasActiveFilters ? 'not-allowed' : 'grab' 
                          }} 
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
                        <Chip 
                          label={question.sequence} 
                          size="small" 
                          color={question.isStart ? "warning" : "default"}
                        />
                      </TableCell>
                      <TableCell sx={{ borderRight: "1px solid #f0f0f0", maxWidth: 200 }}>
                        <Tooltip title={question.clue} arrow>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {question.clue}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
                        <Chip
                          label={question.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={question.isActive ? "success" : "error"}
                          variant={question.isActive ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", borderRight: "1px solid #f0f0f0", maxWidth: 180 }}>
                        {Array.isArray(question.answer) ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                            {question.answer.slice(0, 3).map((ans, idx) => (
                              <Chip
                                key={idx}
                                label={ans}
                                size="small"
                                variant="outlined"
                                color="info"
                              />
                            ))}
                            {question.answer.length > 3 && (
                              <Tooltip title={question.answer.join(', ')} arrow>
                                <Chip
                                  label={`+${question.answer.length - 3} more`}
                                  size="small"
                                  variant="outlined"
                                  color="default"
                                />
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          <Tooltip title={question.answer} arrow>
                            <Typography 
                              variant="body2"
                              sx={{ 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {question.answer}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Edit Question">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleEditQuestion(question)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title={question.isActive ? "Deactivate" : "Activate"}>
                            <IconButton
                              color={question.isActive ? "warning" : "success"}
                              size="small"
                              onClick={() => handleToggleStatus(question._id)}
                            >
                              {question.isActive ? 
                                <VisibilityOffIcon fontSize="small" /> : 
                                <VisibilityIcon fontSize="small" />
                              }
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Question">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => {
                                setOpenModal(true);
                                setDeleteQuestionId(question._id);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Create Question Dialog */}
        <Dialog 
        open={openCreateDialog} 
        onClose={() => setOpenCreateDialog(false)} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
            sx: { minHeight: '80vh', maxHeight: '90vh' }
        }}
        >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
            Create New Question
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
            <Grid container spacing={4}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
                <Box sx={{ height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                    Question Content
                </Typography>
                
                <TextField
                    fullWidth
                    label="Clue"
                    value={createFormData.clue}
                    onChange={(e) => handleCreateFormChange('clue', e.target.value)}
                    multiline
                    rows={4}
                    required
                    variant="outlined"
                    sx={{ mb: 3 }}
                />

                <TextField
                    fullWidth
                    label="Answer"
                    value={createFormData.answer}
                    onChange={(e) => handleCreateFormChange('answer', e.target.value)}
                    required
                    variant="outlined"
                    sx={{ mb: 3 }}
                    multiline
                    rows={3}
                    helperText="For multiple answers, separate with commas: answer1, answer2, answer3"
                />
                
                
                <TextField
                    fullWidth
                    label="Trivia"
                    value={createFormData.trivia}
                    onChange={(e) => handleCreateFormChange('trivia', e.target.value)}
                    multiline
                    rows={4}
                    required
                    variant="outlined"
                    sx={{ mb: 2 }}
                    helperText="Interesting facts shared before clues"
                />
                </Box>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
                <Box sx={{ height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                    Question Settings
                </Typography>
                <TextField
                    fullWidth
                    label="Hint (Optional)"
                    value={createFormData.hint}
                    onChange={(e) => handleCreateFormChange('hint', e.target.value)}
                    multiline
                    rows={2}
                    variant="outlined"
                    sx={{ mb: 3 }}
                    helperText="Optional Fallback hint to help users solve the question"
                />

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Sequence"
                        type="number"
                        value={createFormData.sequence}
                        onChange={(e) => handleCreateFormChange('sequence', parseInt(e.target.value))}
                        required
                        variant="outlined"
                        helperText="Order in question sequence"
                        inputProps={{ min: 1 }}
                    />
                    </Grid>
                </Grid>

                <Paper sx={{ p: 2, backgroundColor: '#f0f7ff' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Status
                    </Typography>
                    <FormControlLabel
                    control={
                        <Switch
                        checked={createFormData.isActive}
                        onChange={(e) => handleCreateFormChange('isActive', e.target.checked)}
                        color="primary"
                        size="medium"
                        />
                    }
                    label={
                        <Box>
                        <Typography variant="body2" fontWeight={500}>
                            {createFormData.isActive ? '✅ Active' : '❌ Inactive'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {createFormData.isActive ? 'Question will be live and available' : 'Question will be hidden from users'}
                        </Typography>
                        </Box>
                    }
                    />
                </Paper>
                </Box>
            </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button 
            onClick={() => setOpenCreateDialog(false)}
            variant="outlined"
            size="large"
            sx={{ mr: 2, px: 4 }}
            >
            Cancel
            </Button>
            <Button 
            onClick={handleSaveNewQuestion} 
            variant="contained"
            size="large"
            sx={{ px: 4 }}
            disabled={!createFormData.clue || !createFormData.trivia || !createFormData.answer || !createFormData.sequence}
            >
            Create Question
            </Button>
        </DialogActions>
        </Dialog>

        {/* Edit Question Dialog */}
        <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
            sx: { minHeight: '80vh', maxHeight: '90vh' }
        }}
        >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
            Edit Question
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
            <Grid container spacing={4}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
                <Box sx={{ height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                    Question Content
                </Typography>
                
                <TextField
                    fullWidth
                    label="Clue"
                    value={editFormData.clue}
                    onChange={(e) => handleFormChange('clue', e.target.value)}
                    multiline
                    rows={4}
                    required
                    variant="outlined"
                    sx={{ mb: 3 }}
                />
                <TextField
                    fullWidth
                    label="Answer"
                    value={editFormData.answer}
                    onChange={(e) => handleFormChange('answer', e.target.value)}
                    required
                    variant="outlined"
                    sx={{ mb: 3 }}
                    multiline
                    rows={3}
                    helperText="For multiple answers, separate with commas: answer1, answer2, answer3"
                />
                <TextField
                    fullWidth
                    label="Trivia"
                    value={editFormData.trivia}
                    onChange={(e) => handleFormChange('trivia', e.target.value)}
                    multiline
                    rows={4}
                    required
                    variant="outlined"
                    sx={{ mb: 2 }}
                    helperText="Interesting facts shared before clues"
                />
                </Box>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
                <Box sx={{ height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                    Question Settings
                </Typography>
                
                <TextField
                    fullWidth
                    label="Hint (Optional)"
                    value={editFormData.hint}
                    onChange={(e) => handleFormChange('hint', e.target.value)}
                    multiline
                    rows={2}
                    variant="outlined"
                    sx={{ mb: 3 }}
                    helperText="Optional Fallback hint to help users solve the question"
                />

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Sequence"
                        type="number"
                        value={editFormData.sequence}
                        onChange={(e) => handleFormChange('sequence', parseInt(e.target.value))}
                        required
                        variant="outlined"
                        helperText="Order in question sequence"
                        inputProps={{ min: 1 }}
                    />
                    </Grid>
                </Grid>

                <Paper sx={{ p: 2, backgroundColor: '#f0f7ff' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Status
                    </Typography>
                    <FormControlLabel
                    control={
                        <Switch
                        checked={editFormData.isActive}
                        onChange={(e) => handleFormChange('isActive', e.target.checked)}
                        color="primary"
                        size="medium"
                        />
                    }
                    label={
                        <Box>
                        <Typography variant="body2" fontWeight={500}>
                            {editFormData.isActive ? '✅ Active' : '❌ Inactive'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {editFormData.isActive ? 'Question is live and available' : 'Question is hidden from users'}
                        </Typography>
                        </Box>
                    }
                    />
                </Paper>
                </Box>
            </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button 
            onClick={() => setOpenEditDialog(false)}
            variant="outlined"
            size="large"
            sx={{ mr: 2, px: 4 }}
            >
            Cancel
            </Button>
            <Button 
            onClick={handleUpdateQuestion} 
            variant="contained"
            size="large"
            sx={{ px: 4 }}
            disabled={!editFormData.clue || !editFormData.trivia || !editFormData.answer || !editFormData.sequence}
            >
            Update Question
            </Button>
        </DialogActions>
        </Dialog>
          
          {/* Delete Confirmation Modal */}
          <Modal
            open={openModal}
            onClose={() => setOpenModal(false)}
            onConfirm={() => handleDeleteQuestion(deleteQuestionId)}
            title="Confirm Deletion"
            body="Are you sure you want to delete this question? This action cannot be undone."
            primaryButtonText="Delete"
            secondaryButtonText="Cancel"
          />
        </>
      )}
    </Box>
  );
};

export default AdminQuestionsManagement;